import React, { forwardRef } from "react";
import classNames from "classnames";

export type IconProps = React.SVGProps<SVGSVGElement>;

type IconDefinition = {
  children: React.ReactNode;
  viewBox?: string;
};

const createIcon = (displayName: string, definition: IconDefinition) => {
  const Icon = forwardRef<SVGSVGElement, IconProps>((props, ref) => {
    const {
      className,
      width = "1em",
      height = "1em",
      fill = "none",
      children,
      ...restProps
    } = props;

    return (
      <svg
        ref={ref}
        className={classNames("componentsui-icon", className)}
        width={width}
        height={height}
        viewBox={definition.viewBox || "0 0 24 24"}
        fill={fill}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
        {...restProps}
      >
        {children || definition.children}
      </svg>
    );
  });

  Icon.displayName = displayName;
  return Icon;
};

const strokeProps = {
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export const CloseIcon = createIcon(
  "CloseIcon",
  {
    children: (
      <>
        <path d="M6 6L18 18" {...strokeProps} />
        <path d="M18 6L6 18" {...strokeProps} />
      </>
    ),
  }
);

export const ChevronLeftIcon = createIcon("ChevronLeftIcon", {
  children: <path d="M15 6L9 12L15 18" {...strokeProps} />,
});

export const ChevronRightIcon = createIcon("ChevronRightIcon", {
  children: <path d="M9 6L15 12L9 18" {...strokeProps} />,
});

export const ChevronDownIcon = createIcon("ChevronDownIcon", {
  children: <path d="M6 9L12 15L18 9" {...strokeProps} />,
});

export const SearchIcon = createIcon("SearchIcon", {
  children: (
    <>
      <path d="M10.5 18C14.6421 18 18 14.6421 18 10.5C18 6.35786 14.6421 3 10.5 3C6.35786 3 3 6.35786 3 10.5C3 14.6421 6.35786 18 10.5 18Z" {...strokeProps} />
      <path d="M16 16L21 21" {...strokeProps} />
    </>
  ),
});

export const DeleteIcon = createIcon("DeleteIcon", {
  children: (
    <>
      <path d="M4 7H20" {...strokeProps} />
      <path d="M10 11V17" {...strokeProps} />
      <path d="M14 11V17" {...strokeProps} />
      <path d="M6 7L7 20H17L18 7" {...strokeProps} />
      <path d="M9 7V4H15V7" {...strokeProps} />
    </>
  ),
});

export const CopyIcon = createIcon("CopyIcon", {
  children: (
    <>
      <path d="M8 8H19V19H8V8Z" {...strokeProps} />
      <path d="M5 16H4V4H16V5" {...strokeProps} />
    </>
  ),
});

export const LockIcon = createIcon("LockIcon", {
  children: (
    <>
      <path d="M6 11H18V20H6V11Z" {...strokeProps} />
      <path d="M8 11V8C8 5.79086 9.79086 4 12 4C14.2091 4 16 5.79086 16 8V11" {...strokeProps} />
    </>
  ),
});

export const EyeIcon = createIcon("EyeIcon", {
  children: (
    <>
      <path d="M2.5 12C4.6 7.9 7.8 6 12 6C16.2 6 19.4 7.9 21.5 12C19.4 16.1 16.2 18 12 18C7.8 18 4.6 16.1 2.5 12Z" {...strokeProps} />
      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" {...strokeProps} />
    </>
  ),
});

export const EyeInvisibleIcon = createIcon("EyeInvisibleIcon", {
  children: (
    <>
      <path d="M3 3L21 21" {...strokeProps} />
      <path d="M9.9 5.3C10.57 5.1 11.27 5 12 5C16.2 5 19.4 7.2 21.5 12C20.88 13.42 20.08 14.63 19.11 15.62" {...strokeProps} />
      <path d="M14.12 14.12C13.58 14.66 12.83 15 12 15C10.34 15 9 13.66 9 12C9 11.17 9.34 10.42 9.88 9.88" {...strokeProps} />
      <path d="M6.73 6.73C4.97 7.8 3.56 9.55 2.5 12C4.6 16.8 7.8 19 12 19C13.48 19 14.84 18.73 16.07 18.18" {...strokeProps} />
    </>
  ),
});

export const SettingsIcon = createIcon("SettingsIcon", {
  children: (
    <>
      <path d="M12 15.5C13.933 15.5 15.5 13.933 15.5 12C15.5 10.067 13.933 8.5 12 8.5C10.067 8.5 8.5 10.067 8.5 12C8.5 13.933 10.067 15.5 12 15.5Z" {...strokeProps} />
      <path d="M19.4 15A1.65 1.65 0 0 0 20 13.7V10.3A1.65 1.65 0 0 0 19.4 9L17.8 7.8L17.5 5.8A1.65 1.65 0 0 0 15.9 4.5H13.9L12 3.5L10.1 4.5H8.1A1.65 1.65 0 0 0 6.5 5.8L6.2 7.8L4.6 9A1.65 1.65 0 0 0 4 10.3V13.7A1.65 1.65 0 0 0 4.6 15L6.2 16.2L6.5 18.2A1.65 1.65 0 0 0 8.1 19.5H10.1L12 20.5L13.9 19.5H15.9A1.65 1.65 0 0 0 17.5 18.2L17.8 16.2L19.4 15Z" {...strokeProps} />
    </>
  ),
});

export const UserIcon = createIcon("UserIcon", {
  children: (
    <>
      <path d="M12 12C14.2091 12 16 10.2091 16 8C16 5.79086 14.2091 4 12 4C9.79086 4 8 5.79086 8 8C8 10.2091 9.79086 12 12 12Z" {...strokeProps} />
      <path d="M4.5 20C5.5 16.6 8.2 15 12 15C15.8 15 18.5 16.6 19.5 20" {...strokeProps} />
    </>
  ),
});

export const AppsIcon = createIcon("AppsIcon", {
  children: (
    <>
      <path d="M5 5H10V10H5V5Z" {...strokeProps} />
      <path d="M14 5H19V10H14V5Z" {...strokeProps} />
      <path d="M5 14H10V19H5V14Z" {...strokeProps} />
      <path d="M14 14H19V19H14V14Z" {...strokeProps} />
    </>
  ),
});

export const CalendarIcon = createIcon("CalendarIcon", {
  children: (
    <>
      <path d="M5 5H19V20H5V5Z" {...strokeProps} />
      <path d="M8 3V7" {...strokeProps} />
      <path d="M16 3V7" {...strokeProps} />
      <path d="M5 10H19" {...strokeProps} />
    </>
  ),
});

export const CalendarClockIcon = createIcon("CalendarClockIcon", {
  children: (
    <>
      <path d="M5 5H19V13" {...strokeProps} />
      <path d="M8 3V7" {...strokeProps} />
      <path d="M16 3V7" {...strokeProps} />
      <path d="M5 10H19" {...strokeProps} />
      <path d="M5 5V20H12" {...strokeProps} />
      <path d="M17 21C19.2091 21 21 19.2091 21 17C21 14.7909 19.2091 13 17 13C14.7909 13 13 14.7909 13 17C13 19.2091 14.7909 21 17 21Z" {...strokeProps} />
      <path d="M17 15.5V17.2L18.2 18" {...strokeProps} />
    </>
  ),
});

export const RefreshIcon = createIcon("RefreshIcon", {
  children: (
    <>
      <path d="M20 6V11H15" {...strokeProps} />
      <path d="M4 18V13H9" {...strokeProps} />
      <path d="M18.2 9C17.2 6.6 14.8 5 12 5C8.7 5 5.95 7.35 5.3 10.45" {...strokeProps} />
      <path d="M5.8 15C6.8 17.4 9.2 19 12 19C15.3 19 18.05 16.65 18.7 13.55" {...strokeProps} />
    </>
  ),
});

export const ExclamationCircleIcon = createIcon("ExclamationCircleIcon", {
  children: (
    <>
      <path d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z" {...strokeProps} />
      <path d="M12 7.5V12.5" {...strokeProps} />
      <path d="M12 16.5V16.51" {...strokeProps} />
    </>
  ),
});

export const EditIcon = createIcon("EditIcon", {
  children: (
    <>
      <path d="M4 20H8L18.5 9.5L14.5 5.5L4 16V20Z" {...strokeProps} />
      <path d="M13.5 6.5L17.5 10.5" {...strokeProps} />
      <path d="M19.5 7.5C20.3284 6.67157 20.3284 5.32843 19.5 4.5C18.6716 3.67157 17.3284 3.67157 16.5 4.5L14.5 6.5L18.5 10.5L19.5 9.5" {...strokeProps} />
    </>
  ),
});

export const QuestionCircleIcon = createIcon("QuestionCircleIcon", {
  children: (
    <>
      <path d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z" {...strokeProps} />
      <path d="M9.8 9.25C10.15 8.15 10.95 7.5 12.1 7.5C13.6 7.5 14.7 8.4 14.7 9.85C14.7 10.8 14.2 11.45 13.1 12.15C12.3 12.65 12 13.15 12 14" {...strokeProps} />
      <path d="M12 17V17.01" {...strokeProps} />
    </>
  ),
});

export const EmptyStateIcon = createIcon("EmptyStateIcon", {
  children: (
    <>
      <path d="M5 9L8 5H16L19 9V19H5V9Z" {...strokeProps} />
      <path d="M5 9H9.5L10.8 12H13.2L14.5 9H19" {...strokeProps} />
      <path d="M9 16H15" {...strokeProps} />
    </>
  ),
});

export const ExclamationIcon = createIcon("ExclamationIcon", {
  children: (
    <>
      <path d="M12 5.5V13" {...strokeProps} />
      <path d="M12 17.5V17.51" {...strokeProps} />
    </>
  ),
});

export const ArrowFallIcon = createIcon("ArrowFallIcon", {
  children: (
    <>
      <path d="M12 4V16" {...strokeProps} />
      <path d="M7 11L12 16L17 11" {...strokeProps} />
      <path d="M6 20H18" {...strokeProps} />
    </>
  ),
});

export const CheckCircleFillIcon = createIcon("CheckCircleFillIcon", {
  children: (
    <>
      <path
        d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z"
        fill="currentColor"
      />
      <path
        d="M8.2 12.2L10.7 14.7L16 9.4"
        stroke="#fff"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
});

export const CustomerServiceIcon = createIcon("CustomerServiceIcon", {
  children: (
    <>
      <path d="M5 13V11C5 7.13401 8.13401 4 12 4C15.866 4 19 7.13401 19 11V13" {...strokeProps} />
      <path d="M5 13C5 11.8954 5.89543 11 7 11H8V17H7C5.89543 17 5 16.1046 5 15V13Z" {...strokeProps} />
      <path d="M16 11H17C18.1046 11 19 11.8954 19 13V15C19 16.1046 18.1046 17 17 17H16V11Z" {...strokeProps} />
      <path d="M19 15V16C19 18.2091 17.2091 20 15 20H13" {...strokeProps} />
      <path d="M10.5 20H13" {...strokeProps} />
    </>
  ),
});
